import React, { useState, useCallback } from 'react';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';
import logoSrc from '@/assets/snap-chart-logo-hd.png';
import { supabase } from '@/integrations/supabase/client';
import { useShareCode } from '@/hooks/useShareCode';

// Fire-and-forget share logging (instruments the viral loop for the Growth dashboard).
const logShare = (channel) => {
  try { supabase.rpc('log_share_event', { p_kind: 'verdict_card', p_channel: channel }).then(() => {}, () => {}); } catch { /* noop */ }
};

const { FiShare2, FiDownload, FiX, FiTwitter, FiCheck, FiLoader, FiCopy, FiFacebook, FiSmartphone, FiInstagram } = FiIcons;

// Web Share API with file support exists mainly on mobile / some Safari.
const CAN_SHARE_FILES = (() => {
  try {
    return typeof navigator !== 'undefined' && !!navigator.canShare &&
      navigator.canShare({ files: [new File([new Blob()], 't.png', { type: 'image/png' })] });
  } catch { return false; }
})();

const APP_URL = 'https://snap-chart.ai';

// ─── Reel generator feature flag ────────────────────────────────────────
// The Creatomate-powered reel generator currently uses a generic "5 Facts
// Story" template that doesn't produce viral-grade TikTok content. Hide
// the button on prod until we have a properly designed template (Fiverr
// commission or custom JSON Source). Backend (/api/generate-reel) stays
// live so we can test renders manually before re-exposing the UI.
const SHOW_REEL_GENERATOR = false;

// Hashtags by asset class — kept short (Twitter rewards focused tag sets).
// Last tag pair is the brand + category, always appended.
const HASHTAGS_BY_MARKET = {
  gold:    ['#XAUUSD', '#Gold', '#Forex', '#PropFirm', '#FTMO'],
  crypto:  ['#Bitcoin', '#BTC', '#Crypto', '#PropFirm', '#FTMO'],
  indices: ['#NAS100', '#SPX', '#DayTrading', '#Indices'],
  forex:   ['#Forex', '#FX', '#Trading', '#PropFirm'],
  other:   ['#Trading', '#PriceAction'],
};

// Per-asset overrides for the crypto pool (so $ETH posts use #ETH, $SOL uses #SOL, etc.).
const SYMBOL_OVERRIDE = {
  ETHUSD:  ['#Ethereum', '#ETH', '#Crypto', '#Trading'],
  ETH:     ['#Ethereum', '#ETH', '#Crypto', '#Trading'],
  SOLUSD:  ['#Solana', '#SOL', '#Crypto', '#Trading'],
  SOL:     ['#Solana', '#SOL', '#Crypto', '#Trading'],
  XRPUSD:  ['#XRP', '#Ripple', '#Crypto', '#Trading'],
  US30:    ['#US30', '#DowJones', '#Indices', '#DayTrading'],
  SPP500:  ['#SPX', '#SP500', '#Indices', '#DayTrading'],
  SPX:     ['#SPX', '#SP500', '#Indices', '#DayTrading'],
  GER30:  ['#DAX', '#GER30', '#Indices', '#DayTrading'],
};

function pickHashtags(symbol, market) {
  const sym = (symbol || '').toUpperCase();
  // Always append: brand tag + community challenge tag (every share contributes
  // to the #SnapChartChallenge growth-stream).
  const tail = ['#SnapChartAI', '#SnapChartChallenge'];
  if (SYMBOL_OVERRIDE[sym]) return [...SYMBOL_OVERRIDE[sym], ...tail];
  const base = HASHTAGS_BY_MARKET[(market || '').toLowerCase()] || HASHTAGS_BY_MARKET.other;
  return [...base, ...tail];
}

function formatPrice(p) {
  if (p == null || isNaN(Number(p))) return '';
  return Number(p).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// First-person opener — creates curiosity ("why is he not trading?"). Optionally
// embeds the current price ($XAUUSD at $4,140) when analysisResult.current_price
// is available.
function buildHook(status, symbol, price) {
  const sym = symbol ? `$${symbol.toUpperCase()}` : 'This setup';
  const at = price ? ` at $${formatPrice(price)}` : '';
  switch (status) {
    case 'NO_TRADE':
    case 'WAIT':
      return `${sym}${at} — not trading this.`;
    case 'AUTHORIZED':
      return `${sym}${at} — taking this setup.`;
    case 'WATCH':
    case 'NEUTRAL':
    default:
      return `${sym}${at} — watching this.`;
  }
}

// Verdict-aware question to bait engagement (replies on Twitter, comments on
// Instagram). The whole strategy: invite traders to compare their own read
// vs the AI's, so they reply -> their followers see the conversation -> reach.
function buildQuestion(status) {
  switch (status) {
    case 'NO_TRADE':
    case 'WAIT':
      return 'What does your AI say? 👇';
    case 'AUTHORIZED':
      return "I'm taking this. You? 👇";
    case 'WATCH':
    case 'NEUTRAL':
    default:
      return "What's your read? 👇";
  }
}

// One-liner that re-anchors the brand voice after the reason+confidence row.
function buildClosing(status) {
  switch (status) {
    case 'NO_TRADE':
    case 'WAIT':
      return 'Discipline wins.';
    case 'AUTHORIZED':
      return 'Plan in the card.';
    default:
      return 'Verdict in the card.';
  }
}

// Tight reason for the tweet body — primaryReason can be up to 180 chars in the
// UI, but a tweet line should stay short. Cut at ~80 chars on a word boundary.
function shortenReason(text) {
  if (!text) return '';
  const t = String(text).trim().replace(/\s+/g, ' ');
  if (t.length <= 80) return t;
  const cut = t.slice(0, 77);
  const space = cut.lastIndexOf(' ');
  return (space > 40 ? cut.slice(0, space) : cut) + '…';
}

// Verdict → display config. Mirrors AnalysisQuickSummary status values.
const VERDICTS = {
  AUTHORIZED: { word: 'TRADE', sub: 'AUTHORIZED', color: '#10b981', glow: 'rgba(16,185,129,0.45)' },
  WAIT:       { word: 'WAIT',  sub: 'STRUCTURE NOT CONFIRMED', color: '#a855f7', glow: 'rgba(168,85,247,0.45)' },
  NO_TRADE:   { word: 'NO-TRADE', sub: 'CONDITIONS NOT MET', color: '#f59e0b', glow: 'rgba(245,158,11,0.45)' },
  WATCH:      { word: 'WATCH', sub: 'MONITORING', color: '#22d3ee', glow: 'rgba(34,211,238,0.45)' },
  NEUTRAL:    { word: 'WATCH', sub: 'MONITORING', color: '#22d3ee', glow: 'rgba(34,211,238,0.45)' },
};

const SIZE = 1080;
const F = '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif';

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
  const words = (text || '').split(/\s+/);
  let line = '';
  let lines = 0;
  for (let i = 0; i < words.length; i++) {
    const test = line ? `${line} ${words[i]}` : words[i];
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y);
      line = words[i];
      y += lineHeight;
      lines++;
      if (lines >= maxLines - 1) {
        let last = `${line} ${words.slice(i + 1).join(' ')}`.trim();
        while (ctx.measureText(`${last}…`).width > maxWidth && last.length) last = last.slice(0, -1);
        ctx.fillText(last ? `${last}…` : line, x, y);
        return;
      }
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, y);
}

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

// Draw an image cover-fit inside a rounded rect (object-fit: cover), clipped.
function drawCover(ctx, img, rx, ry, rw, rh, radius) {
  ctx.save();
  roundRect(ctx, rx, ry, rw, rh, radius);
  ctx.clip();
  const scale = Math.max(rw / img.width, rh / img.height);
  const dw = img.width * scale, dh = img.height * scale;
  ctx.drawImage(img, rx + (rw - dw) / 2, ry + (rh - dh) / 2, dw, dh);
  ctx.restore();
}

/**
 * Premium share card — v3 2026 design language to match the dashboard
 * VerdictShareCard. Embeds the user's chart with the verdict overlaid.
 * Returns a PNG Blob.
 *
 * Visual elements (v3):
 *  - Radial gradient background (cyan/purple) + tone-tinted center glow
 *  - Dot grid texture overlay
 *  - Gradient "S" logo tile + brand wordmark + LIVE pulse pill (top strip)
 *  - Hero chart with darkening overlay + tone-colored verdict badge glow
 *  - Big tagline: "Anyone can take a trade. Professionals know when NOT to."
 *  - Cyan snap-chart.ai wordmark + "try free" hint (bottom)
 */
async function drawCard({ verdict, symbol, market, bias, confidence, reason, chartSrc }) {
  const [chart, logo] = await Promise.all([loadImage(chartSrc), loadImage(logoSrc)]);

  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');

  // ────────── Background base ──────────
  const bg = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  bg.addColorStop(0, '#0a0e1a');
  bg.addColorStop(0.5, '#0f172a');
  bg.addColorStop(1, '#0a0e1a');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // ────────── Subtle corner glows ONLY (kept far from the chart hero area) ──────────
  // The corners give the v3 look without bleeding over the chart screenshot
  // or the verdict badge sitting in the middle/bottom of the card.
  const drawGlow = (cx0, cy0, r0, color) => {
    const g = ctx.createRadialGradient(cx0, cy0, 0, cx0, cy0, r0);
    g.addColorStop(0, color);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, SIZE, SIZE);
  };
  // Top corners only — chart area (y 160-720) stays clean
  drawGlow(SIZE - 60, 40, 220, 'rgba(34,211,238,0.12)');
  drawGlow(60, 40, 220, 'rgba(168,85,247,0.10)');
  // Bottom (under chart + above tagline) only
  drawGlow(80, SIZE - 60, 260, 'rgba(168,85,247,0.10)');
  drawGlow(SIZE - 80, SIZE - 60, 260, 'rgba(34,211,238,0.10)');

  // ────────── Top strip: gradient logo + brand + LIVE pulse pill ──────────
  const headerY = 60;
  // Gradient "S" tile (matches dashboard share card)
  const tileX = 60, tileY = headerY, tileSize = 64;
  const tileGrad = ctx.createLinearGradient(tileX, tileY, tileX + tileSize, tileY + tileSize);
  tileGrad.addColorStop(0, '#22d3ee');
  tileGrad.addColorStop(1, '#a855f7');
  ctx.fillStyle = tileGrad;
  roundRect(ctx, tileX, tileY, tileSize, tileSize, 14); ctx.fill();
  // Glow under tile
  ctx.save();
  ctx.shadowColor = 'rgba(34,211,238,0.4)';
  ctx.shadowBlur = 24;
  roundRect(ctx, tileX, tileY, tileSize, tileSize, 14); ctx.fill();
  ctx.restore();
  // "S" letter on the tile
  ctx.textAlign = 'center';
  ctx.fillStyle = '#0a0e1a';
  ctx.font = `900 38px ${F}`;
  ctx.fillText('S', tileX + tileSize / 2, tileY + tileSize / 2 + 14);

  // Brand wordmark (gradient text)
  ctx.textAlign = 'left';
  const brandX = tileX + tileSize + 18;
  const brandGrad = ctx.createLinearGradient(brandX, 0, brandX + 320, 0);
  brandGrad.addColorStop(0, '#a855f7');
  brandGrad.addColorStop(1, '#22d3ee');
  ctx.fillStyle = brandGrad;
  ctx.font = `900 38px ${F}`;
  ctx.fillText('SnapChart AI', brandX, headerY + 30);
  // Sub-label mono uppercase
  ctx.fillStyle = '#64748b';
  ctx.font = `700 16px ui-monospace, ${F}`;
  ctx.fillText('AI RISK OFFICER · 2S VERDICT', brandX, headerY + 56);

  // LIVE pulse pill (top right)
  const pillW = 180, pillH = 36, pillX = SIZE - 60 - pillW, pillY = headerY + 16;
  ctx.fillStyle = 'rgba(16,185,129,0.12)';
  roundRect(ctx, pillX, pillY, pillW, pillH, 10); ctx.fill();
  ctx.strokeStyle = 'rgba(16,185,129,0.35)';
  ctx.lineWidth = 1.5;
  roundRect(ctx, pillX, pillY, pillW, pillH, 10); ctx.stroke();
  // Pulse dot
  ctx.fillStyle = '#10b981';
  ctx.beginPath(); ctx.arc(pillX + 16, pillY + pillH / 2, 5, 0, Math.PI * 2); ctx.fill();
  ctx.save();
  ctx.shadowColor = '#10b981'; ctx.shadowBlur = 12;
  ctx.beginPath(); ctx.arc(pillX + 16, pillY + pillH / 2, 5, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
  // Pill label
  ctx.fillStyle = '#6ee7b7';
  ctx.font = `700 13px ui-monospace, ${F}`;
  ctx.textAlign = 'left';
  ctx.fillText('LIVE · AI VERDICT', pillX + 30, pillY + pillH / 2 + 5);

  // ---- Chart hero (or fallback panel) ----
  const rx = 60, ry = 160, rw = SIZE - 120, rh = 560, rr = 28;
  if (chart) {
    drawCover(ctx, chart, rx, ry, rw, rh, rr);
  } else {
    // No screenshot — draw a generated sparkline so the card never looks empty.
    ctx.fillStyle = 'rgba(255,255,255,0.02)';
    roundRect(ctx, rx, ry, rw, rh, rr); ctx.fill();
    ctx.save();
    roundRect(ctx, rx, ry, rw, rh, rr); ctx.clip();
    const n = 30, pad = 60;
    const pts = [];
    let v = 0.45;
    for (let i = 0; i < n; i++) {
      v += Math.sin(i * 0.6) * 0.16 + (i % 6 === 0 ? 0.14 : -0.04);
      v = Math.max(0.08, Math.min(0.92, v));
      pts.push([rx + pad + (rw - pad * 2) * (i / (n - 1)), ry + rh - pad - (rh - pad * 2) * v]);
    }
    const grad = ctx.createLinearGradient(0, ry, 0, ry + rh);
    grad.addColorStop(0, verdict.color + '4D');
    grad.addColorStop(1, verdict.color + '00');
    ctx.beginPath();
    ctx.moveTo(pts[0][0], ry + rh);
    pts.forEach((p) => ctx.lineTo(p[0], p[1]));
    ctx.lineTo(pts[n - 1][0], ry + rh);
    ctx.closePath();
    ctx.fillStyle = grad; ctx.fill();
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    pts.forEach((p) => ctx.lineTo(p[0], p[1]));
    ctx.strokeStyle = verdict.color; ctx.lineWidth = 4; ctx.lineJoin = 'round'; ctx.stroke();
    ctx.restore();
  }
  // Legibility overlay (darken toward bottom)
  ctx.save();
  roundRect(ctx, rx, ry, rw, rh, rr); ctx.clip();
  const og = ctx.createLinearGradient(0, ry, 0, ry + rh);
  og.addColorStop(0, 'rgba(11,17,32,0.15)');
  og.addColorStop(0.45, 'rgba(11,17,32,0.0)');
  og.addColorStop(0.78, 'rgba(11,17,32,0.55)');
  og.addColorStop(1, 'rgba(11,17,32,0.95)');
  ctx.fillStyle = og;
  ctx.fillRect(rx, ry, rw, rh);
  ctx.restore();
  // Border
  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.lineWidth = 2;
  roundRect(ctx, rx, ry, rw, rh, rr); ctx.stroke();

  // Symbol pill (top-left on chart)
  ctx.textAlign = 'left';
  const symText = market ? `${symbol}  ·  ${market}` : (symbol || 'CHART');
  ctx.font = `700 30px ${F}`;
  const symW = ctx.measureText(symText).width + 44;
  ctx.fillStyle = 'rgba(2,6,23,0.7)';
  roundRect(ctx, rx + 28, ry + 28, symW, 56, 14); ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillText(symText, rx + 50, ry + 64);

  // Verdict badge (bottom-left on chart)
  const bw = 360, bh = 132, bx = rx + 36, by = ry + rh - bh - 36;
  ctx.save();
  ctx.shadowColor = verdict.glow;
  ctx.shadowBlur = 50;
  ctx.fillStyle = 'rgba(2,6,23,0.72)';
  roundRect(ctx, bx, by, bw, bh, 22); ctx.fill();
  ctx.restore();
  ctx.strokeStyle = verdict.color;
  ctx.lineWidth = 3;
  roundRect(ctx, bx, by, bw, bh, 22); ctx.stroke();
  ctx.fillStyle = verdict.color;
  ctx.font = `900 72px ${F}`;
  ctx.fillText(verdict.word, bx + 28, by + 76);
  ctx.fillStyle = '#cbd5e1';
  ctx.font = `600 19px ${F}`;
  ctx.fillText(verdict.sub, bx + 28, by + 108);

  // Confidence (bottom-right on chart)
  if (confidence) {
    const cyB = by + 40;
    ctx.textAlign = 'right';
    ctx.fillStyle = '#e2e8f0';
    ctx.font = `800 56px ${F}`;
    ctx.fillText(`${confidence}%`, rx + rw - 40, cyB);
    ctx.fillStyle = '#94a3b8';
    ctx.font = `600 20px ${F}`;
    ctx.fillText('CONFIDENCE', rx + rw - 40, cyB + 30);
    // bar
    const barW = 260, barH = 12, barX = rx + rw - 40 - barW, barY = cyB + 50;
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    roundRect(ctx, barX, barY, barW, barH, 6); ctx.fill();
    ctx.fillStyle = verdict.color;
    roundRect(ctx, barX, barY, Math.max(12, barW * Math.min(100, confidence) / 100), barH, 6); ctx.fill();
    ctx.textAlign = 'left';
  }

  // Bias (under chart, left)
  let y = ry + rh + 60;
  ctx.textAlign = 'left';
  if (bias && bias !== 'NEUTRAL') {
    const biasColor = bias === 'LONG' ? '#10b981' : bias === 'SHORT' ? '#ef4444' : '#22d3ee';
    ctx.fillStyle = biasColor;
    ctx.font = `800 34px ${F}`;
    ctx.fillText(bias, 60, y);
  }

  // Reason
  if (reason) {
    ctx.fillStyle = '#94a3b8';
    ctx.font = `400 28px ${F}`;
    wrapText(ctx, reason, bias && bias !== 'NEUTRAL' ? 180 : 60, y, SIZE - 240, 38, 2);
  }

  // ---- v3 Tagline (above footer) ----
  ctx.textAlign = 'center';
  ctx.fillStyle = '#cbd5e1';
  ctx.font = `italic 600 26px ${F}`;
  ctx.fillText('"Anyone can take a trade.', SIZE / 2, 928);
  // Build the second line with the cyan-highlighted "NOT" inline
  const line2Pre = '"Professionals know when ';
  const line2Mid = 'NOT';
  const line2Post = ' to."';
  ctx.font = `italic 600 26px ${F}`;
  const wPre = ctx.measureText(line2Pre).width;
  const wMid = ctx.measureText(line2Mid).width;
  const wPost = ctx.measureText(line2Post).width;
  const totalW = wPre + wMid + wPost;
  let drawX = SIZE / 2 - totalW / 2;
  ctx.textAlign = 'left';
  ctx.fillStyle = '#cbd5e1';
  ctx.fillText(line2Pre, drawX, 962); drawX += wPre;
  ctx.fillStyle = '#22d3ee';
  ctx.font = `700 26px ${F}`;
  ctx.fillText(line2Mid, drawX, 962); drawX += wMid;
  ctx.fillStyle = '#cbd5e1';
  ctx.font = `italic 600 26px ${F}`;
  ctx.fillText(line2Post, drawX, 962);

  // ---- Footer: brand on left + URL on right (v3 style) ----
  ctx.textAlign = 'left';
  ctx.fillStyle = '#64748b';
  ctx.font = `600 16px ui-monospace, ${F}`;
  ctx.fillText('SHARED · NOT FINANCIAL ADVICE', 60, 1018);

  ctx.textAlign = 'right';
  ctx.fillStyle = '#22d3ee';
  ctx.font = `900 32px ${F}`;
  ctx.fillText('snap-chart.ai', SIZE - 60, 1018);

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png', 0.95));
}

// Import the v3 dashboard share card so /trading share matches /dashboard share
// (visual consistency requested by user: 'ces pas les nouvelle card, regarde ceux du dashboard').
import VerdictShareCardV3 from '../VerdictShareCard';
import { useAuth as useAuthSC } from '../../contexts/AuthContext';

const ShareAnalysisCard = ({ decision, analysisResult, chartImage }) => {
  const { code: shareCode } = useShareCode();
  const { user: scUser } = useAuthSC();
  // Per-user ref code in the share URL; falls back to "share" if the RPC
  // didn't return one yet (e.g., very first render before lazy generation).
  const refParam = shareCode || 'share';
  const shareUrl = `${APP_URL}/?ref=${refParam}`;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [copied, setCopied] = useState(false);
  const [instagramPrepped, setInstagramPrepped] = useState(false);
  // Reel generation state (Creatomate)
  const [reelBusy, setReelBusy] = useState(false);
  const [reelUrl, setReelUrl] = useState(null);
  const [reelError, setReelError] = useState(null);

  const status = decision?.status || 'WATCH';
  if (status === 'ERROR') return null;

  const verdict = VERDICTS[status] || VERDICTS.WATCH;
  const symbol = analysisResult?.symbol || '';
  const market = analysisResult?.market || '';
  const price = analysisResult?.current_price;
  const hashtags = pickHashtags(symbol, market).join(' ');

  // Reason line: "Structure not confirmed. 60% confidence." — confidence and
  // verdict word are already on the card image, so the tweet doesn't repeat
  // them. We use only the reason + confidence here.
  const reasonShort = shortenReason(decision?.primaryReason || decision?.primaryReasonFull);
  const confPart = decision?.confidence ? `${decision.confidence}% confidence.` : '';
  let reasonLine = '';
  if (reasonShort && confPart) reasonLine = `${reasonShort.replace(/[.!?]+$/, '')}. ${confPart}`;
  else if (reasonShort) reasonLine = reasonShort.replace(/[.!?]+$/, '') + '.';
  else if (confPart) reasonLine = confPart;

  const caption = [
    buildHook(status, symbol, price),
    '',
    reasonLine,
    buildClosing(status),
    '',
    buildQuestion(status),
    '',
    'The AI that tells you when NOT to trade.',
    `${shareUrl} — try free`,
    '',
    hashtags,
  ].filter((line, i, arr) => {
    // Drop reasonLine if empty so we don't leave two blank lines back-to-back.
    if (line === '' && arr[i - 1] === '') return false;
    return line !== null;
  }).join('\n');

  const generate = useCallback(async () => {
    setBusy(true);
    try {
      const b = await drawCard({
        verdict,
        symbol,
        market,
        bias: decision?.bias,
        confidence: decision?.confidence,
        reason: decision?.primaryReason || decision?.primaryReasonFull,
        chartSrc: chartImage || null,
      });
      setBlob(b);
      setImgUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(b); });
      setOpen(true);
    } catch (e) {
      console.error('Share card generation failed:', e);
    } finally {
      setBusy(false);
    }
  }, [verdict, symbol, market, decision, chartImage]);

  const fileName = `snapchart-${(symbol || 'analysis').toLowerCase().replace(/[^a-z0-9]/g, '')}.png`;

  const nativeShare = async () => {
    if (!blob) return;
    logShare('native');
    const file = new File([blob], fileName, { type: 'image/png' });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Snap Chart AI', text: caption });
        return;
      }
    } catch (e) { if (e?.name === 'AbortError') return; }
    download();
  };

  const download = () => {
    if (!imgUrl) return;
    const a = document.createElement('a');
    a.href = imgUrl;
    a.download = fileName;
    a.click();
  };

  const handleDownload = () => { logShare('download'); download(); };

  const copyImage = async () => {
    if (!blob || !navigator.clipboard?.write) return;
    try {
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
      logShare('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { console.error('Copy image failed:', e); }
  };

  const postOnX = () => {
    logShare('x');
    download(); // X intent can't attach images — download so the user attaches it
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`, '_blank', 'noopener');
  };

  const postOnFacebook = () => {
    logShare('facebook');
    download();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(caption)}`, '_blank', 'noopener');
  };

  // Instagram has no web posting intent (their TOS forbids it). The best we can
  // do in one click: copy the caption, download the image, and try to open the
  // Instagram app via the instagram:// deep-link (mobile) or instagram.com (web).
  // The user pastes the caption + uploads the image manually.
  const postOnInstagram = async () => {
    logShare('instagram');
    // 1) Copy caption to clipboard so the user can paste it directly in IG.
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(caption);
      }
    } catch (e) { console.warn('[share] clipboard copy failed:', e?.message); }
    // 2) Download the image so the user has it ready in their Photos / Downloads.
    download();
    // 3) Try to open Instagram. On mobile with the app installed, the deep-link
    //    fires. On desktop or without the app, fall back to instagram.com.
    setInstagramPrepped(true);
    setTimeout(() => setInstagramPrepped(false), 4000);
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
    if (isMobile) {
      // Try app deep-link first; if it fails, the browser navigates to the web fallback.
      window.location.href = 'instagram://camera';
      setTimeout(() => {
        window.open('https://www.instagram.com', '_blank', 'noopener');
      }, 800);
    } else {
      window.open('https://www.instagram.com', '_blank', 'noopener');
    }
  };

  // ─── Reel generator (Creatomate via Vercel route) ─────────────────────
  // Triggers a 15-30s vertical (9:16) MP4 render with the verdict info.
  // Render takes ~15-25s server-side; we show a spinner then the preview.
  const generateReel = async () => {
    setReelBusy(true);
    setReelError(null);
    setReelUrl(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setReelError('Please log in first.');
        setReelBusy(false);
        return;
      }
      logShare('reel_generate');
      const resp = await fetch('/api/generate-reel', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          verdict: status,
          symbol,
          price,
          confidence: decision?.confidence,
        }),
      });
      const data = await resp.json();
      if (resp.ok && data.success && data.videoUrl) {
        setReelUrl(data.videoUrl);
      } else if (resp.status === 202 && data.renderId) {
        setReelError('Render still in progress. Try again in 20 seconds.');
      } else {
        setReelError(data.message || data.error || 'Reel generation failed.');
      }
    } catch (e) {
      setReelError(e?.message || 'Network error.');
    } finally {
      setReelBusy(false);
    }
  };

  // Map the analyze decision/result into the shape VerdictShareCardV3 expects.
  // The dashboard share card uses these tokens (matching the dashboard color rules):
  //   - TRADE       -> emerald
  //   - WAIT        -> amber
  //   - NO-TRADE    -> purple
  //   - WATCH/other -> gray
  const verdictTone = (() => {
    const s = (status || '').toUpperCase().replace('_', '-');
    if (s === 'AUTHORIZED' || s === 'TRADE') return 'emerald';
    if (s === 'WAIT' || s === 'CONDITIONAL') return 'amber';
    if (s === 'NO-TRADE') return 'purple';
    return 'gray';
  })();
  const verdictWord = (() => {
    const s = (status || '').toUpperCase();
    if (s === 'AUTHORIZED') return 'TRADE';
    if (s === 'NO_TRADE') return 'NO-TRADE';
    return s || 'WAIT';
  })();
  const verdictForCard = {
    asset: symbol,
    price,
    verdict: verdictWord,
    conf: decision?.confidence,
    _tone: verdictTone,
  };
  const userHandleSC = scUser?.email?.split('@')[0] || 'snapchartai';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-cyan-500/40 hover:border-cyan-400/60 transition-all flex items-center gap-2"
        title="Share this verdict as an image"
      >
        <div className="p-1.5 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30">
          <SafeIcon icon={FiShare2} className="w-4 h-4 text-cyan-400" />
        </div>
        <span className="text-sm font-medium text-white">Share</span>
      </button>

      {open && (
        <VerdictShareCardV3
          verdict={verdictForCard}
          onClose={() => setOpen(false)}
          userHandle={userHandleSC}
        />
      )}
    </>
  );
};

export default ShareAnalysisCard;
